import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { dataVisService } from "../api/client";
import { useUserPreferences } from "./UserPreferencesContext";
import { useDataEnvironment } from "./DataEnvironmentContext";
import { enrichSingleEventTreeWithMonitorChildren } from "../utils/singleEventTreeLoads";

export interface TreeItem {
    children: TreeItem[];
    className: string;
    node_type: string;
    label: string;
    description?: string | null;
    id: string;
    db_id: string;
    template_id: string;
    template_name: string;
    category_id: string;
    location_name: string;
    consumer_name: string;
    status: string;
    agent_id?: number;
    instance_id?: number | string | null;
    monitor_key?: string | null;
    monitor_kind?: string | null;
    owner_group_id?: number | string | null;
    owner_group_name?: string | null;
    secondary_label?: string;
    event_device_id?: number | string | null;
    assignments?: Array<{
        id?: number | string | null;
        virtual_device_id?: number | string | null;
        assignment_name?: string | null;
        load_name?: string | null;
        location?: string | null;
        category_id?: number | string | null;
        zone_id?: number | string | null;
        zone_name?: string | null;
        event_id?: number | string | null;
        event_name?: string | null;
        monitor_id?: number | string | null;
        monitor_label?: string | null;
        inventory_device_id?: number | string | null;
        inventory_device_name?: string | null;
        notes?: string | null;
    }>;
}

export type TreeType = "devices" | "events" | "venues" | "single-event" | "agents" | "unassigned-devices";

export type TreeFilter = {
    tableName: string;
    key: string;
    value: unknown;
}

type TreeContextType = {
    expanded: Set<string>;
    setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>;
    toggle: (id: string) => void;
    treeItems: TreeItem[];
    setTreeItems: React.Dispatch<React.SetStateAction<TreeItem[]>>;
    treeType: TreeType;
    setTreeType: React.Dispatch<React.SetStateAction<TreeType>>;
    treeFilter: TreeFilter;
    setTreeFilter: React.Dispatch<React.SetStateAction<TreeFilter>>;
    anchorEl: any;
    setAnchorEl: any;
    propsPopoverAnchorPosition: { top: number; left: number } | null;
    setPropsPopoverAnchorPosition: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>;
    selectedCardId: string | null;
    setSelectedCardId: (id: string | null) => void;
    newlyCreatedId: string | null;
    setNewlyCreatedId: React.Dispatch<React.SetStateAction<string | null>>;
    editDialogOpen: boolean;
    setEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
    editFormData: any;
    setEditFormData: React.Dispatch<React.SetStateAction<any>>;
    fetchItems: (overrideTreeType?: TreeType, overrideTreeFilter?: TreeFilter) => Promise<TreeItem[]>;
};

const TreeContext = React.createContext<TreeContextType>({
    expanded: new Set<string>(),
    setExpanded: () => { },
    toggle: () => { },
    treeItems: [],
    setTreeItems: () => { },
    treeType: "devices",
    setTreeType: () => { },
    treeFilter: { tableName: "", key: "", value: "" },
    setTreeFilter: () => { },
    anchorEl: null,
    setAnchorEl: () => { },
    propsPopoverAnchorPosition: null,
    setPropsPopoverAnchorPosition: () => { },
    selectedCardId: null,
    setSelectedCardId: () => { },
    newlyCreatedId: null,
    setNewlyCreatedId: () => { },
    editDialogOpen: false,
    setEditDialogOpen: () => { },
    editFormData: {},
    setEditFormData: () => { },
    fetchItems: () => Promise.resolve([]),
});

// Helper function to find all ancestors of a node (for expandVisi)
const findAncestors = (items: TreeItem[], targetId: string, ancestors: string[] = []): string[] | null => {
    for (const item of items) {
        if (item.id === targetId) {
            return ancestors;
        }
        if (item.children && item.children.length > 0) {
            const found = findAncestors(item.children, targetId, [...ancestors, item.id]);
            if (found) return found;
        }
    }
    return null;
};

const getTreePreferenceKey = (treeType: TreeType, dataEnvironment: string) =>
    `${dataEnvironment}:${treeType}`;

export const TreeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { preferences, isLoaded, setTreeSelected } = useUserPreferences();
    const { dataEnvironment } = useDataEnvironment();
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [treeItems, setTreeItems] = useState<TreeItem[]>([]);
    const [treeType, setTreeType] = useState<TreeType>("devices");
    const [treeFilter, setTreeFilter] = useState<TreeFilter>({ tableName: "", key: "", value: "" });
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const [propsPopoverAnchorPosition, setPropsPopoverAnchorPosition] = useState<{ top: number; left: number } | null>(null);
    const [selectedCardIdState, setSelectedCardIdState] = useState<string | null>(null);
    const [selectedCardScope, setSelectedCardScope] = useState(() => `${dataEnvironment}:devices`);
    const currentSelectionScope = `${dataEnvironment}:${treeType}`;
    const selectedCardId = selectedCardScope === currentSelectionScope ? selectedCardIdState : null;
    const setSelectedCardId = useCallback((id: string | null) => {
        setSelectedCardScope(currentSelectionScope);
        setSelectedCardIdState(id);
        if (id === null) {
            setTreeSelected(currentSelectionScope, undefined);
        }
    }, [currentSelectionScope, setTreeSelected]);
    const [newlyCreatedId, setNewlyCreatedId] = useState<string | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [editFormData, setEditFormData] = useState({
        description: "",
        location_name: "",
        consumer_name: "",
        status: "",
        name: "",
    });

    const fetchItems = useCallback(async (overrideTreeType?: TreeType, overrideTreeFilter?: TreeFilter): Promise<TreeItem[]> => {

        // console.log("fetchItems: overrideTreeType =", overrideTreeType);
        // console.log("fetchItems: treeType =", treeType);

        const effectiveTreeType = overrideTreeType ?? treeType;

        console.log("fetchItems: effectiveTreeType =", effectiveTreeType);

        // Call API with correct treeType
        const response = await dataVisService.getTreeview(effectiveTreeType, overrideTreeFilter ?? treeFilter);
        return effectiveTreeType === "single-event"
            ? enrichSingleEventTreeWithMonitorChildren(response)
            : response;
    }, [treeFilter, treeType]);

    // useEffect(() => {
    //     if (anchorEl) {
    //         console.log("TreeContext: anchorEl:", {
    //             tag: anchorEl.tagName,
    //             isVisible: anchorEl.offsetWidth > 0 && anchorEl.offsetHeight > 0,
    //         });
    //     } else {
    //         console.log("TreeContext: anchorEl: null");
    //     }
    // }, [anchorEl]);

    useEffect(() => {
        setNewlyCreatedId(null);
    }, [dataEnvironment, treeType]);

    useEffect(() => {
        const saved = localStorage.getItem(`tree-expanded:${getTreePreferenceKey(treeType, dataEnvironment)}`);
        if (saved) {
            setExpanded(new Set(JSON.parse(saved)));
            return;
        }

        setExpanded(new Set());
    }, [dataEnvironment, treeType]);

    useEffect(() => {
        localStorage.setItem(
            `tree-expanded:${getTreePreferenceKey(treeType, dataEnvironment)}`,
            JSON.stringify([...expanded])
        );
    }, [dataEnvironment, expanded, treeType]);

    // Restore selected node from preferences when tree items load
    useEffect(() => {
        // Don't restore from preferences if we just created a new item
        // or if this tree already has an explicit active selection.
        if (newlyCreatedId || selectedCardId) {
            return;
        }

        if (isLoaded && treeItems.length > 0 && preferences.treeSelected) {
            const scopedKey = getTreePreferenceKey(treeType, dataEnvironment);
            const savedSelection = preferences.treeSelected[scopedKey];

            // Only process if there's a saved selection
            if (savedSelection) {
                // Validate that the saved selection exists in the current tree
                const ancestors = findAncestors(treeItems, savedSelection);
                if (ancestors) {
                    // Node exists - restore selection and expand ancestors
                    setExpanded(prev => {
                        const newExpanded = new Set(prev);
                        ancestors.forEach(ancestorId => newExpanded.add(ancestorId));
                        return newExpanded;
                    });
                    setSelectedCardId(savedSelection);
                } else {
                    // Node doesn't exist in current tree (e.g., switched to different event)
                    // Clear selection silently (no warning needed on initial load)
                    setSelectedCardId(null);
                }
            }
        }
    }, [dataEnvironment, isLoaded, treeItems, treeType, preferences.treeSelected, newlyCreatedId, selectedCardId, setSelectedCardId]);

    // Save selected node to preferences when it changes
    // Only save if the new selection differs from what's already saved
    useEffect(() => {
        if (isLoaded && selectedCardId) {
            const scopedKey = getTreePreferenceKey(treeType, dataEnvironment);
            const currentSaved = preferences.treeSelected?.[scopedKey];
            if (currentSaved !== selectedCardId) {
                setTreeSelected(scopedKey, selectedCardId);
            }
        }
    }, [dataEnvironment, selectedCardId, treeType, isLoaded, setTreeSelected, preferences.treeSelected]);

    const toggle = (id: string) => {
        setExpanded(prev => {
            const copy = new Set(prev);
            copy.has(id) ? copy.delete(id) : copy.add(id);
            return copy;
        });
    };

    const contextValue = useMemo(() => ({
            expanded, setExpanded, toggle, treeItems, setTreeItems, anchorEl, setAnchorEl, propsPopoverAnchorPosition, setPropsPopoverAnchorPosition, selectedCardId, setSelectedCardId,
            newlyCreatedId, setNewlyCreatedId,
            treeType, setTreeType, treeFilter, setTreeFilter, editDialogOpen, setEditDialogOpen, editFormData, setEditFormData, fetchItems
        }), [
            anchorEl,
            editDialogOpen,
            editFormData,
            expanded,
            setExpanded,
            fetchItems,
            newlyCreatedId,
            propsPopoverAnchorPosition,
            selectedCardId,
            setSelectedCardId,
            treeFilter,
            treeItems,
            treeType,
        ]);

    return (
        <TreeContext.Provider value={contextValue}>
            {children}
        </TreeContext.Provider>
    );
};

export const useTreeContext = () => {
    const ctx = useContext(TreeContext);
    if (!ctx) throw new Error("useTreeContext must be used inside TreeProvider");
    return ctx;
};
